import { Injectable } from "@angular/core";
import {
    IAddress,
    ICustomer, ICustomerAgency, IDistributorAgency, IIntroduction, IQuote, ITotal1, ITotal2, ITraveller,
    IWatabiLightBooking
} from "libraries/interfaces/watabilight/IWatabiLightBooking.interface";
import _cloneDeep from "lodash-es/cloneDeep";

@Injectable()
export class WatabiPostDataFormattingService {
    public formateData(_value: any, booking: IWatabiLightBooking): IWatabiLightBooking {
        const value: any = _cloneDeep(_value);
        const newBooking: IWatabiLightBooking = {};
        const customer: ICustomer = this.setCustomer(value.customer, booking);
        const travellers: ITraveller[] = this.setTraveller(value.traveller, booking);
        const distributorAgency: IDistributorAgency = this.setCustomerAgency(value.agency, booking.customerAgency);
        const quote: IQuote[] = this.setQuote(value.price, booking.quotes);
        if (Object.keys(customer).length !== 0) {
            newBooking.customer = customer;
        }
        if (travellers.length !== 0) {
            newBooking.travellers = travellers;
        }
        if (Object.keys(distributorAgency).length !== 0) {
            newBooking.distributorAgency = distributorAgency;
        }
        if (Object.keys(quote).length !== 0) {
            newBooking.quotes = quote;
        }
        if (value.reference?.agencyTitle) {
            newBooking.agencyTitle = value.reference?.agencyTitle;
        }

        return newBooking;
    }

    private setQuote(newQuote:any, oldQuote: IQuote[]): IQuote[] {
        const retQuote: IQuote[] = [];
        oldQuote.forEach((quote: IQuote) => {
            retQuote.push(this.setQuoteId(newQuote[quote.idItem], quote));
        });
        return retQuote;
    }

    private setQuoteId(newQuote:any, quote: IQuote): IQuote {
        const valQuote: IQuote = quote;
        valQuote.price.retailAgency.total = this.setPrice(newQuote.total);
        valQuote.price.retailAgency.totalCommission = this.setPrice(newQuote.commission);
        valQuote.price.retailAgency.profit = this.setPrice(newQuote.margin);

        valQuote.price.retailAgency.traveller = [];
        Object.keys(newQuote.travellers).forEach((id: string) => {
            valQuote.price.retailAgency.traveller.push({
                "id": id,
                "total": this.setPrice(newQuote.travellers[id]),
            });
        });
        return valQuote;
    }

    private setPrice(value: number): any {
        if (!value) {
            return null;
        }
        return {
            value: value * 100,
            amount: value,
            currency: "EUR",
            decimals: "2"
        };
    }

    private setCustomerAgency(newAgency: any, oldAgency: ICustomerAgency): IDistributorAgency {
        const agencyFormat: IDistributorAgency = {
            customerAgency: {}
        };
        this.setLogo(agencyFormat, newAgency, "logo");

        const address: IAddress = {};
        this.setValueNameArray(address, newAgency.address, "country");
        this.setValueNameArray(address, newAgency.address, "city");
        this.setValue(address, newAgency.address, "zipCode");
        this.setValue(address, newAgency.address, "nameStreet", "streetLineArray");

        this.deleteIfSame(oldAgency.address, address, ["country","nameArray", "0", "value"]);
        this.deleteIfSame(oldAgency.address, address, ["city", "nameArray","0", "value"]);
        this.deleteIfSame(oldAgency.address, address, "zipCode");
        this.deleteIfSame(oldAgency.address, address, "streetLineArray");

        agencyFormat.customerAgency.address = address;

        this.setTextArray(agencyFormat.customerAgency, newAgency, "mail", "emailArray");
        this.setTextArray(agencyFormat.customerAgency, newAgency, "phone", "telephoneArray");
        this.setValueItem(agencyFormat.customerAgency, newAgency, "name");

        this.deleteIfSame(oldAgency, agencyFormat.customerAgency, ["telephoneArray", "0","text"]);
        this.deleteIfSame(oldAgency, agencyFormat.customerAgency, ["emailArray", "0","text"]);
        this.deleteIfSame(oldAgency, agencyFormat.customerAgency, "name");

        return agencyFormat;
    }

    private setCustomer(customer: any, booking: IWatabiLightBooking): any {
        const customerFormat: ICustomer = {};

        this.setValue(customerFormat, customer, "title");
        this.setValue(customerFormat, customer, "firstName");
        this.setValue(customerFormat, customer, "lastName");
        this.setValue(customerFormat, customer, "mail", "email");
        this.setValue(customerFormat, customer, "phone");

        const customerAddress: IAddress = {};
        this.setValue(customerAddress, customer.address, "zipCode");
        this.setValue(customerAddress, customer.address, "nameStreet", "streetLineArray");
        this.setValueNameArray(customerAddress, customer.address, "city");
        this.setValueNameArray(customerAddress, customer.address, "country");

        customerFormat.address = customerAddress;
        return (customerFormat);
    }

    private setTraveller(travellers: any, booking: IWatabiLightBooking): ITraveller[] {
        const newTravellers: ITraveller[] = [];
        Object.keys(travellers).forEach((key: string) => {
            const addTraveller: ITraveller = this.setTravellerId(travellers[key], booking.travellers.find(item => item.id === key));
            newTravellers.push(addTraveller);
        });
        return newTravellers;
    }

    private setTravellerId(traveller: any, bookingTraveller: ITraveller): ITraveller {
        const newTraveller: ITraveller = {};
        newTraveller.type = bookingTraveller.type;
        newTraveller.id = bookingTraveller.id;
        newTraveller.xId = bookingTraveller.xId;

        this.setValue(newTraveller, traveller, "title");
        this.setValue(newTraveller, traveller, "lastName");
        this.setValue(newTraveller, traveller, "firstName");
        this.setValue(newTraveller, traveller, "birthDate");

        return newTraveller;
    }

    private setValue(item: any, value: any, key: string, key2: string = key): void {
        if (value && value[key]) {
            item[key2] = value[key];
        }
    }

    private setValueNameArray(item: any, value: any, key: string, key2: string = key): void {
        if (value && value[key]) {
            item[key2] = {
                "nameArray": [
                    {
                        "value": value[key],
                        "languagueCode": "FR"
                    }
                ]
            };
        }
    }

    private checkIfSame(itemOri: any, itemCheck: any, key: string[]): boolean {
        const editKey: string[] = Object.assign([], key);
        if (editKey.length === 1 && JSON.stringify(itemOri[key[0]]) === JSON.stringify(itemCheck[key[0]])) {
            return true;
        }
        else if (editKey.length > 1 && itemCheck[editKey[0]] && itemOri[editKey[0]]) {
            let index: string = editKey[0];
            editKey.shift();
            return this.checkIfSame(itemOri[index], itemCheck[index], editKey);
        }
        return false;
    }

    private deleteIfSame(itemOri: any, itemCheck: any, key: string | string[]): void {
        if (Array.isArray(key)) {
            if (this.checkIfSame(itemOri, itemCheck, key)) {
                delete itemCheck[key[0]];
            }
        }
        else {
            if (JSON.stringify(itemOri[key]) === JSON.stringify(itemCheck[key])) {
                delete itemCheck[key];
            }
        }
    }


    private setTextArray(item: any, value: any, key: string, key2: string = key): void {
        if (value && value[key]) {
            item[key2] = [{"text": value[key]}];
        }
    }

    private setValueItem(item: any, value: any, key: string, key2: string = key): void {
        if (value && value[key]) {
            item[key2] = {"value": value[key]};
        }
    }

    private setLogo(item: any, value: any, key: string): void {
        if (value && value[key]) {
            item[key] = {
                "url": value[key]
            };
        }
    }

}
